import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Monitor, MapPin, Phone, Zap, Lock } from 'lucide-react';
import CountdownTimer from '../components/CountdownTimer';
import ProgressBar from '../components/ProgressBar';
import TierTable from '../components/TierTable';
import DealParticipationModal from '../components/DealParticipationModal';
import ComingSoonBanner from '../components/ComingSoonBanner';
import { getDeal, getImageUrl, BUSINESS, DEALS_TEASER_MODE, DEALS_TEASER_MESSAGE } from '../config';

export default function DealDetail() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  const loadDeal = useCallback(async () => {
    try {
      const data = await getDeal(id);
      setDeal(data);
    } catch (e) {
      console.error('Failed to load deal', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDeal();
    document.title = `Deal | ${BUSINESS.name}`;
  }, [loadDeal]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-slate-600">Deal introuvable</p>
        <Link to="/deals" className="text-secondary hover:underline">Retour aux deals</Link>
      </div>
    );
  }

  const product = deal.product || {};
  const images = product.images || [];
  const isActive = deal.status === 'active';

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link to="/deals" className="inline-flex items-center gap-2 text-slate-600 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Tous les deals</span>
        </Link>

        {/* Teaser banner */}
        {DEALS_TEASER_MODE && (
          <div className="mb-8">
            <ComingSoonBanner
              title="Section en cours de construction"
              message={DEALS_TEASER_MESSAGE}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Image */}
          <div>
            <div className="aspect-square rounded-sm overflow-hidden bg-slate-100 border border-slate-200">
              {images[imgIdx] ? (
                <img
                  src={getImageUrl(images[imgIdx])}
                  alt={deal.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Monitor className="w-24 h-24 text-gray-600" />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                      i === imgIdx ? 'border-secondary' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Info */}
          <div className="space-y-6">
            {/* Status + countdown */}
            <div className="flex items-center gap-3">
              {isActive && (
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-secondary/10 text-secondary border border-secondary/25">
                  Deal en cours
                </span>
              )}
              {isActive && deal.expires_at && (
                <CountdownTimer expiresAt={deal.expires_at} />
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading tracking-tight">
              {deal.title}
            </h1>

            {deal.description && (
              <p className="text-slate-600 leading-relaxed">{deal.description}</p>
            )}

            {/* Current price */}
            <div className="flex items-baseline gap-3">
              <span className="font-heading font-bold text-3xl text-secondary">
                {(deal.current_price || deal.base_price).toFixed(2)} €
              </span>
              {deal.current_price && deal.current_price < deal.base_price && (
                <span className="text-lg text-slate-500 line-through">{deal.base_price.toFixed(2)} €</span>
              )}
              <span className="text-sm text-slate-500">/ unite TTC</span>
            </div>

            {/* Progress */}
            {isActive && deal.min_threshold && (
              <ProgressBar
                current={deal.confirmed_quantity || 0}
                target={deal.min_threshold}
              />
            )}

            {/* Tier table */}
            <TierTable
              tiers={deal.tiers}
              basePrice={deal.base_price}
              activeTier={deal.active_tier}
            />

            {/* CTA */}
            {DEALS_TEASER_MODE ? (
              <button
                type="button"
                disabled
                className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-600 font-semibold rounded-full py-3.5 text-lg border border-slate-200 cursor-not-allowed"
              >
                <Lock className="w-5 h-5" />
                Bientôt disponible
              </button>
            ) : isActive && (
              <button
                onClick={() => setShowModal(true)}
                className="w-full flex items-center justify-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-lg hover:bg-secondary transition-colors shadow-md"
              >
                <Zap className="w-5 h-5" />
                Rejoindre ce deal
              </button>
            )}

            {/* Product specs */}
            {(product.processor || product.ram || product.storage || product.screen_size) && (
              <div className="bg-white/[0.02] border border-slate-200 rounded-sm p-4">
                <h4 className="text-sm font-semibold text-white font-heading mb-3">Caracteristiques</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {product.processor && (
                    <div><span className="text-slate-500">Processeur</span><br /><span className="text-slate-700">{product.processor}</span></div>
                  )}
                  {product.ram && (
                    <div><span className="text-slate-500">RAM</span><br /><span className="text-slate-700">{product.ram}</span></div>
                  )}
                  {product.storage && (
                    <div><span className="text-slate-500">Stockage</span><br /><span className="text-slate-700">{product.storage}</span></div>
                  )}
                  {product.screen_size && (
                    <div><span className="text-slate-500">Ecran</span><br /><span className="text-slate-700">{product.screen_size}</span></div>
                  )}
                </div>
              </div>
            )}

            {/* Addons */}
            {deal.addons?.length > 0 && (
              <div className="bg-white/[0.02] border border-slate-200 rounded-sm p-4">
                <h4 className="text-sm font-semibold text-white font-heading mb-3">Accessoires disponibles</h4>
                <div className="space-y-2">
                  {deal.addons.map((addon) => (
                    <div key={addon.product_id} className="flex justify-between text-sm">
                      <span className="text-slate-700">{addon.name}</span>
                      <span className="text-secondary font-medium">{addon.price?.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pickup info */}
            <div className="bg-white/[0.02] border border-slate-200 rounded-sm p-4 space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700">{BUSINESS.adresse}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-secondary shrink-0" />
                <span className="text-sm text-slate-700">{BUSINESS.telephone}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <DealParticipationModal
          deal={deal}
          onClose={() => setShowModal(false)}
          onSuccess={loadDeal}
        />
      )}
    </div>
  );
}
